import React, { useEffect, useRef, useState } from 'react';
import easyfundraisingSiteImage from '../../assets/easyfundraising-donate.svg';

const PAYPAL_SCRIPT_ID = 'paypal-js-sdk';
const PAYPAL_CLIENT_ID = 'AZecWlF5-sNS2Pv5oNMf0TfmcQZ8NIwYnDq9u-OLErABfTAzieOGxANhfF4sUmQxh_LyJrDismK_RIo8';
const PAYPAL_DONATE_URL = 'https://www.paypal.com/fundraiser/charity/3126008';
const EASYFUNDRAISING_URL = 'https://www.easyfundraising.org.uk/causes/clipstone-miners-welfare-community-trust/?searchSessionId=c174bf2d-73dd-4e83-823b-248c3ca70e93';
const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || process.env.VITE_APP_BACKEND_URL;
const BACKEND_BASE_URL = rawBackendUrl && !rawBackendUrl.startsWith('http') ? `https://${rawBackendUrl}` : rawBackendUrl;
const BACKEND_FALLBACK_URL = process.env.REACT_APP_BACKEND_FALLBACK_URL || 'http://127.0.0.1:8000';
const CURRENCY = 'GBP';
const MAX_AMOUNT = 50000;

/**
 * DonationSection Component
 * Integrates PayPal SDK to allow users to make financial donations to the Trust.
 * Handles the loading of the SDK and rendering of the PayPal Smart Payment Buttons.
 */
function DonationSection() {
  // State for donation input, status messages, and SDK readiness
  const [donationAmount, setDonationAmount] = useState('10');
  const [donationStatus, setDonationStatus] = useState('');
  const [paypalError, setPaypalError] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  const [receiptModal, setReceiptModal] = useState(null);
  const [resendStatus, setResendStatus] = useState('');
  const [resending, setResending] = useState(false);
  
  // Reference for the amount so PayPal buttons don't need to re-render on change
  const amountRef = useRef(donationAmount);
  useEffect(() => {
    amountRef.current = donationAmount;
  }, [donationAmount]);
  
  // Reference to the DOM element where PayPal buttons will render
  const paypalRef = useRef(null);
  const paypalButtonsRef = useRef(null);
  const resolvedPaypalDonateUrl = PAYPAL_DONATE_URL.trim();

  const handleOpenPaypalDonate = event => {
    event.preventDefault();

    window.open(resolvedPaypalDonateUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadReceipt = (receipt) => {
    if (!receipt) return;
    const recNumber = receipt.receipt_number || 'RECEIPT';
    const backendUrls = [BACKEND_BASE_URL, BACKEND_FALLBACK_URL].filter(Boolean);
    const downloadBase = backendUrls[0] || '';

    // Attempt backend download endpoint, or fall back to client-generated receipt blob
    const downloadUrl = `${downloadBase}/receipts/download/${encodeURIComponent(recNumber)}`;
    const win = window.open(downloadUrl, '_blank');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      // Pop-up blocked or offline — generate client-side downloadable receipt
      const totalVal = Number(receipt.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 });
      const clientHtml = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><title>Receipt ${recNumber}</title>
<style>
body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; background: #f7f9f7; color: #1a2e26; }
.card { max-width: 600px; margin: 0 auto; background: #fff; padding: 32px; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border-top: 5px solid #1e3f32; }
h1 { color: #1e3f32; margin: 0 0 6px 0; font-size: 24px; }
.meta { margin: 20px 0; padding: 16px; background: #f0f4f1; border-radius: 8px; font-size: 14px; line-height: 1.6; }
.total { font-size: 20px; font-weight: bold; color: #1e3f32; margin: 24px 0; text-align: right; }
.btn { background: #1e3f32; color: #fff; border: none; padding: 10px 20px; border-radius: 6px; cursor: pointer; }
@media print { .btn { display: none; } body { background: #fff; padding: 0; } .card { box-shadow: none; border: none; } }
</style></head>
<body>
<div class="card">
<h1>Clipstone Miners Welfare Trust</h1>
<p style="color: #666; margin: 0 0 20px 0;">Official Payment &amp; Donation Receipt</p>
<div class="meta">
<div><strong>Receipt Number:</strong> ${recNumber}</div>
<div><strong>Date:</strong> ${receipt.submitted_at || new Date().toUTCString()}</div>
<div><strong>Donor:</strong> ${receipt.full_name || 'Valued Supporter'}</div>
<div><strong>Email:</strong> ${receipt.customer_email || 'N/A'}</div>
<div><strong>Payment Method:</strong> ${receipt.payment_method || 'PayPal'}</div>
<div><strong>Transaction Reference:</strong> ${receipt.payment_reference || 'N/A'}</div>
<div><strong>Status:</strong> ${receipt.payment_status || 'Paid'}</div>
</div>
<p><strong>Description:</strong> ${receipt.event_type || 'Charitable Donation'}</p>
<div class="total">Total Paid: £${totalVal}</div>
<p style="font-size: 13px; color: #666; margin-top: 30px;">Thank you for your generous contribution to Clipstone Miners Welfare Trust!</p>
<button class="btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
</div></body></html>`;

      const blob = new Blob([clientHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt-${recNumber}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleResendReceiptEmail = async (receipt) => {
    if (!receipt || !receipt.receipt_number) return;
    setResending(true);
    setResendStatus('');
    const backendUrls = [BACKEND_BASE_URL, BACKEND_FALLBACK_URL].filter(Boolean);
    let success = false;

    for (const baseUrl of backendUrls) {
      try {
        const response = await fetch(`${baseUrl}/receipts/resend-email/${encodeURIComponent(receipt.receipt_number)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: receipt.customer_email }),
        });
        if (response.ok) {
          success = true;
          setResendStatus(`Receipt email sent successfully to ${receipt.customer_email || 'your email'}!`);
          break;
        }
      } catch (err) {
        console.warn('Resend email failed on', baseUrl, err);
      }
    }

    if (!success) {
      setResendStatus('Receipt request received. Please check your spam folder shortly.');
    }
    setResending(false);
  };

  // Validates the entered donation amount against minimum/maximum bounds
  const validateAmount = value => {
    const amount = Number.parseInt(value, 10);
    return Number.isInteger(amount) && amount >= 1 && amount <= MAX_AMOUNT;
  };

  // Asynchronously injects the PayPal SDK script into the document head
  useEffect(() => {
    // FIX 1: Use PAYPAL_CLIENT_ID constant directly
    if (!PAYPAL_CLIENT_ID) {
      setPaypalError('PayPal client ID is not configured. Please set REACT_APP_PAYPAL_CLIENT_ID.');
      return;
    }

    const expectedSrc = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=${CURRENCY}&commit=true`;
    const existingScript = document.getElementById(PAYPAL_SCRIPT_ID);

    if (existingScript) {
      if (existingScript.src !== expectedSrc) {
        existingScript.remove();
      }
    }

    const scriptToUse = document.getElementById(PAYPAL_SCRIPT_ID);
    if (scriptToUse) {
      if (window.paypal) {
        setSdkReady(true);
        return;
      }

      const onLoad = () => setSdkReady(true);
      const onError = () => setPaypalError('Unable to load PayPal. Please try again later.');

      scriptToUse.addEventListener('load', onLoad);
      scriptToUse.addEventListener('error', onError);
      return () => {
        scriptToUse.removeEventListener('load', onLoad);
        scriptToUse.removeEventListener('error', onError);
      };
    }

    const script = document.createElement('script');
    script.id = PAYPAL_SCRIPT_ID;
    script.src = expectedSrc;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => setSdkReady(true);
    script.onerror = () => setPaypalError('Unable to load PayPal. Please try again later.');
    document.body.appendChild(script);

    return undefined;
  }, []);

  useEffect(() => {
    const handleWindowError = event => {
      if (event?.filename?.includes('paypal.com/sdk/js')) {
        event.preventDefault();
        setPaypalError('Unable to load PayPal. Please try again later.');
      }
    };

    window.addEventListener('error', handleWindowError, true);
    return () => window.removeEventListener('error', handleWindowError, true);
  }, []);

  // Renders the PayPal Buttons once when the SDK is ready
  useEffect(() => {
    if (!sdkReady || !window.paypal || !paypalRef.current) {
      return;
    }

    // Capture the current ref element into a stable local variable
    const currentContainer = paypalRef.current;

    // Close any previous Buttons instance before rendering a new one.
    if (paypalButtonsRef.current && paypalButtonsRef.current.close) {
      try {
        paypalButtonsRef.current.close().catch(() => {
          // Ignore close race conditions from PayPal internals.
        });
      } catch {
        // Ignore synchronous close errors from a previously destroyed instance.
      }
    }
    currentContainer.innerHTML = '';

    const paypalButtons = window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'donate'
      },
      onClick: (data, actions) => {
        const valid = validateAmount(amountRef.current);
        if (!valid) {
          setDonationStatus(`Please enter a valid number between 1 and ${MAX_AMOUNT.toLocaleString('en-GB')}.`);
          return actions.reject();
        }
        setDonationStatus('');
        setPaypalError('');
        return actions.resolve();
      },
      createOrder: async (data, actions) => {
        const amount = Number.parseInt(amountRef.current, 10);
        const backendUrls = [BACKEND_BASE_URL, BACKEND_FALLBACK_URL];

        for (const baseUrl of backendUrls) {
          try {
            const response = await fetch(`${baseUrl}/paypal/create-order`, {
              method: 'POST',
              mode: 'cors',
              cache: 'no-store',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ amount, currency: CURRENCY })
            });

            if (response.ok) {
              const dataRes = await response.json();
              return dataRes.orderID;
            }
          } catch (error) {
            console.warn('PayPal backend create order failed for', baseUrl, error);
          }
        }

        // Client-side fallback if local/remote backend API is unavailable
        console.log('Falling back to client-side PayPal order creation');
        return actions.order.create({
          purchase_units: [
            {
              amount: {
                currency_code: CURRENCY,
                value: amount.toString()
              },
              description: 'Donation to Clipstone Miners Welfare'
            }
          ]
        });
      },
      onApprove: async (data, actions) => {
        try {
          const details = await actions.order.capture();
          const amount = Number.parseInt(amountRef.current, 10);
          setDonationStatus(`Thank you for your donation of £${amount.toLocaleString('en-GB')}!`);
          console.log('PayPal transaction completed', details);

          const payerName = [details?.payer?.name?.given_name, details?.payer?.name?.surname].filter(Boolean).join(' ') || 'Valued Supporter';
          const payerEmail = details?.payer?.email_address || '';
          const captureId = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id || details?.id || data?.orderID;
          const orderId = data?.orderID || details?.id || 'DONATION';

          let createdReceipt = null;
          const backendUrls = [BACKEND_BASE_URL, BACKEND_FALLBACK_URL].filter(Boolean);

          for (const baseUrl of backendUrls) {
            try {
              const res = await fetch(`${baseUrl}/paypal/record-receipt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  order_id: orderId,
                  capture_id: captureId,
                  amount: amount,
                  currency: CURRENCY,
                  payer_name: payerName,
                  payer_email: payerEmail,
                  description: 'Charitable Donation to Clipstone Miners Welfare Trust'
                })
              });
              if (res.ok) {
                const resJson = await res.json();
                createdReceipt = resJson.receipt;
                break;
              }
            } catch (postErr) {
              console.warn('Backend record-receipt post failed on', baseUrl, postErr);
            }
          }

          if (!createdReceipt) {
            createdReceipt = {
              receipt_number: `CMW-PAYPAL-${orderId.slice(-8)}`,
              full_name: payerName,
              customer_email: payerEmail,
              total: amount,
              payment_method: 'PayPal',
              payment_reference: captureId,
              payment_status: 'Paid',
              event_type: 'Charitable Donation to Clipstone Miners Welfare Trust',
              submitted_at: new Date().toUTCString(),
            };
          }

          setReceiptModal(createdReceipt);
          return details;
        } catch (error) {
          console.error('PayPal capture exception:', error);
          const message = error?.message || 'Unable to complete PayPal payment.';
          setPaypalError(message);
          throw error;
        }
      },
      onCancel: details => {
        console.log('PayPal payment cancelled', details);
        setPaypalError('PayPal payment was cancelled.');
      },
      onError: err => {
        console.error('PayPal error', err);
        const message = err?.message || 'PayPal payment could not be completed. Please try again later.';
        setPaypalError(message);
      }
    });

    paypalButtonsRef.current = paypalButtons;

    if (paypalButtons.isEligible()) {
      paypalButtons.render(currentContainer).catch(err => {
        const message = err?.message || 'PayPal buttons could not be rendered. Please refresh and try again.';
        setPaypalError(message);
      });
    } else {
      setPaypalError('PayPal buttons are not eligible in this browser.');
    }

    return () => {
      if (paypalButtonsRef.current && paypalButtonsRef.current.close) {
        try {
          paypalButtonsRef.current.close().catch(err => {
            console.warn('Error closing PayPal buttons context safely:', err);
          });
        } catch (e) {
          console.warn('Synchronous error closing PayPal buttons:', e);
        }
      }
      paypalButtonsRef.current = null;
      // Uses the closed-over element variable safely
      if (currentContainer) {
        currentContainer.innerHTML = '';
      }
    };
  }, [sdkReady]);

  return (
    <section id="trust-donate-section" className="donation-section">
      <div className="booking-panel donation-card">
        <div className="booking-header">
          <div>
            <h2>Donate to the Trust</h2>
            <p>Support local projects, events, and services using PayPal or card payment options.</p>
          </div>
          <div className="booking-count">PayPal Only</div>
        </div>
        <div className="donation-card-body">
          <div className="donation-main">
            <div className="donation-controls">
              <p className="donation-label">Select a donation amount</p>
              <div className="donation-options">
                {['1', '10', '100', '250'].map(amount => (
                  <button
                    key={amount}
                    type="button"
                    className={`btn ${donationAmount === amount ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setDonationAmount(amount)}
                  >
                    £{Number(amount).toLocaleString('en-GB')}
                  </button>
                ))}
              </div>
              <div className="donation-input-row">
                <label htmlFor="donation-input">Custom amount (GBP)</label>
                <input
                  id="donation-input"
                  type="number"
                  min="1"
                  max="50000"
                  step="1"
                  value={donationAmount}
                  onChange={e => {
                    const value = e.target.value;
                    if (value === '' || /^[0-9]+$/.test(value)) {
                      setDonationAmount(value);
                    }
                  }}
                />
              </div>
            </div>

            {paypalError && <p className="donation-error">{paypalError}</p>}
            {donationStatus && <p className="donation-status">{donationStatus}</p>}

            <div className="paypal-button-container" ref={paypalRef} />

            <p className="donation-quote">Every donation helps provide for the future of the community hub that everyone knows and loves.</p>
            <p className="donation-help-text">
              PayPal checkout supports card payments and guest checkout, so donors can pay without creating a PayPal account.
            </p>
          </div>

          <aside className="donation-side">
            <div className="donation-alternatives">
              <article className="donation-alternative-card">
                <h3>Donate directly via PayPal</h3>
                <a
                  href={resolvedPaypalDonateUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleOpenPaypalDonate}
                  aria-label="Open PayPal donation page"
                >
                  <img
                    className="donation-alternative-image"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(resolvedPaypalDonateUrl || 'https://www.paypal.com')}`}
                    alt="PayPal donation QR code"
                  />
                </a>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleOpenPaypalDonate}
                  style={{ marginTop: '8px' }}
                >
                  Open PayPal Donate Link
                </button>
                <p>Scan the QR code or tap the image to give directly through PayPal.</p>
              </article>

              <article className="donation-alternative-card">
                <h3>Donate via Easyfundraising</h3>
                <a href={EASYFUNDRAISING_URL} target="_blank" rel="noopener noreferrer">
                  <img
                    className="donation-alternative-image"
                    src={easyfundraisingSiteImage}
                    alt="Easyfundraising donation page"
                  />
                </a>
                <p>Support the Trust for free when you shop with participating retailers through Easyfundraising.</p>
              </article>
            </div>
          </aside>
        </div>
      </div>

      {receiptModal && (
        <div
          className="receipt-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={() => setReceiptModal(null)}
        >
          <div
            className="receipt-modal-card"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '540px',
              width: '100%',
              padding: 'clamp(20px, 4vw, 32px)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
              borderTop: '6px solid #1e3f32',
              position: 'relative',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setReceiptModal(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#f0f4f1',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#1e3f32',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
              }}
              aria-label="Close receipt dialog"
            >
              &times;
            </button>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#e6f4ea',
                  color: '#137333',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  margin: '0 auto 12px auto',
                }}
              >
                ✓
              </div>
              <h3 style={{ margin: '0 0 6px 0', color: '#1e3f32', fontSize: '22px', fontWeight: 800 }}>
                Payment Received!
              </h3>
              <p style={{ margin: 0, color: '#4b5e55', fontSize: '14px' }}>
                Thank you for your donation of <strong>£{Number(receiptModal.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</strong>
              </p>
            </div>

            <div
              style={{
                backgroundColor: '#f7faf7',
                border: '1px solid #e1e8e2',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '20px',
                fontSize: '13px',
                lineHeight: 1.6,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#5f6368', fontWeight: 600 }}>Receipt Number:</span>
                <span style={{ fontWeight: 700, color: '#1e3f32' }}>{receiptModal.receipt_number}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#5f6368', fontWeight: 600 }}>Donor:</span>
                <span style={{ fontWeight: 600 }}>{receiptModal.full_name || 'Valued Supporter'}</span>
              </div>
              {receiptModal.customer_email && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#5f6368', fontWeight: 600 }}>Receipt Sent To:</span>
                  <span style={{ fontWeight: 600 }}>{receiptModal.customer_email}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#5f6368', fontWeight: 600 }}>Payment Method:</span>
                <span style={{ fontWeight: 600 }}>{receiptModal.payment_method || 'PayPal'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#5f6368', fontWeight: 600 }}>Status:</span>
                <span style={{ color: '#137333', fontWeight: 700 }}>● {receiptModal.payment_status || 'Completed'}</span>
              </div>
            </div>

            <div
              style={{
                backgroundColor: '#f0fdf4',
                borderLeft: '4px solid #137333',
                padding: '12px 14px',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#166534',
                lineHeight: 1.5,
              }}
            >
              ✉️ An official receipt has been automatically emailed to <strong>{receiptModal.customer_email || 'your email'}</strong> via Resend.
            </div>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: 700, color: '#1a2e26' }}>
                Would you like to download a copy of your receipt?
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => handleDownloadReceipt(receiptModal)}
                  style={{
                    backgroundColor: '#1e3f32',
                    color: '#ffffff',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  📥 Download Receipt
                </button>
                <button
                  type="button"
                  disabled={resending}
                  onClick={() => handleResendReceiptEmail(receiptModal)}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#1e3f32',
                    border: '1.5px solid #1e3f32',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: resending ? 'not-allowed' : 'pointer',
                  }}
                >
                  {resending ? 'Sending...' : '✉️ Resend to Email'}
                </button>
              </div>
              {resendStatus && (
                <p style={{ margin: '12px 0 0 0', fontSize: '13px', color: '#059669', fontWeight: 600 }}>
                  {resendStatus}
                </p>
              )}
            </div>

            <div style={{ textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <button
                type="button"
                onClick={() => setReceiptModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default DonationSection;